<template>
  <div class="space-y-6">
    <!-- Header de la pestaña de Inventario -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white rounded-xl shadow-sm border border-gray-100 p-4">
      <div>
        <h2 class="text-base font-bold text-gray-900 flex items-center gap-2">
          <i class="fa fa-boxes text-orange-500" />
          <span>Relación de Inventarios, Rotación y Agotamiento</span>
        </h2>
        <p class="text-xs text-gray-500">
          Analiza qué productos se recargan con mayor frecuencia y cuáles tienen mayor velocidad de agotamiento.
        </p>
      </div>

      <div class="flex items-center gap-2">
        <a
          :href="getPdfUrl('inventory')"
          target="_blank"
          class="px-3.5 py-2 bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm"
        >
          <i class="fa fa-file-pdf text-rose-600" />
          <span>Exportar Inventario PDF</span>
        </a>
      </div>
    </div>

    <!-- Valorización Global del Inventario (KPIs de Inventario) -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div class="text-xs font-bold uppercase tracking-wider text-gray-400">Items en Catálogo</div>
        <div class="text-xl font-black text-gray-900 mt-1">
          {{ formatNumber(inventory.valuation.total_items) }}
        </div>
        <div class="text-[11px] text-gray-500 mt-1">Productos registrados</div>
      </div>

      <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div class="text-xs font-bold uppercase tracking-wider text-gray-400">Unidades Físicas Totales</div>
        <div class="text-xl font-black text-orange-600 mt-1">
          {{ formatNumber(inventory.valuation.total_physical_units) }}
        </div>
        <div class="text-[11px] text-gray-500 mt-1">Existencias en almacén</div>
      </div>

      <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div class="text-xs font-bold uppercase tracking-wider text-gray-400">Valor Total a Costo</div>
        <div class="text-xl font-black text-gray-800 mt-1">
          {{ formatCurrency(inventory.valuation.total_cost_value) }}
        </div>
        <div class="text-[11px] text-gray-500 mt-1">Capital inmovilizado</div>
      </div>

      <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div class="text-xs font-bold uppercase tracking-wider text-gray-400">Valor Total a Venta</div>
        <div class="text-xl font-black text-emerald-600 mt-1">
          {{ formatCurrency(inventory.valuation.total_sale_value) }}
        </div>
        <div class="text-[11px] text-emerald-700 font-semibold mt-1">
          Margen pot.: {{ formatCurrency(inventory.valuation.potential_margin) }}
        </div>
      </div>
    </div>

    <!-- SECCIÓN 1: PRODUCTOS QUE MÁS SE RECARGAN (Requisito Explícito) -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div class="p-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div class="flex items-center gap-2.5">
          <div class="w-8 h-8 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center">
            <i class="fa fa-sync-alt text-sm" />
          </div>
          <div>
            <h3 class="text-sm font-bold text-gray-900">
              ¿Cuál es el producto que más se recarga?
            </h3>
            <p class="text-xs text-gray-500">Ranking por volumen de unidades ingresadas mediante lotes y compras</p>
          </div>
        </div>

        <span class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-orange-50 text-orange-700 border border-orange-200">
          Mayor Reposición de Stock
        </span>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full text-left text-xs">
          <thead class="bg-gray-50 text-gray-500 uppercase font-bold text-[10px] border-b border-gray-100">
            <tr>
              <th class="py-3 px-4 text-center" style="width: 5%;">#</th>
              <th class="py-3 px-4" style="width: 35%;">Producto</th>
              <th class="py-3 px-3">Código</th>
              <th class="py-3 px-3 text-right">Unid. Recargadas</th>
              <th class="py-3 px-3 text-center">Frecuencia</th>
              <th class="py-3 px-3 text-right">Stock Actual</th>
              <th class="py-3 px-3">Almacén de Ingreso</th>
              <th class="py-3 px-4">Última Recarga</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr
              v-for="(item, idx) in inventory.most_recharged"
              :key="item.id"
              class="hover:bg-orange-50/20 transition-colors"
            >
              <td class="py-3 px-4 text-center font-bold text-gray-400">
                <span
                  class="w-5 h-5 rounded-full inline-flex items-center justify-center text-[10px]"
                  :class="idx === 0 ? 'bg-orange-500 text-white font-black shadow-sm' : idx < 3 ? 'bg-orange-100 text-orange-800 font-bold' : 'bg-gray-100 text-gray-600'"
                >
                  {{ idx + 1 }}
                </span>
              </td>
              <td class="py-3 px-4">
                <div class="font-bold text-gray-900">{{ item.name }}</div>
                <div class="text-[10px] text-gray-400 font-semibold">{{ item.category }}</div>
              </td>
              <td class="py-3 px-3 font-mono text-gray-500">
                {{ item.code }}
              </td>
              <td class="py-3 px-3 text-right font-mono font-bold text-orange-600 text-sm">
                {{ formatNumber(item.units_recharged) }}
              </td>
              <td class="py-3 px-3 text-center">
                <span class="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-50 text-blue-700">
                  {{ item.recharge_events }} {{ item.recharge_events === 1 ? 'vez' : 'veces' }}
                </span>
              </td>
              <td class="py-3 px-3 text-right font-mono font-bold text-gray-900">
                {{ formatNumber(item.current_stock) }}
              </td>
              <td class="py-3 px-3 text-gray-600">
                <i class="fa fa-warehouse text-gray-400 mr-1 text-[10px]" />
                {{ item.deposit_name }}
              </td>
              <td class="py-3 px-4 text-gray-500 font-mono text-[11px]">
                {{ item.last_recharge_date || '-' }}
              </td>
            </tr>
            <tr v-if="!inventory.most_recharged?.length">
              <td colspan="8" class="text-center py-6 text-gray-400">
                No hay historial de recargas de inventario en este período.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- SECCIÓN 2: PRODUCTOS QUE MÁS RÁPIDO SE ACABAN (Requisito Explícito) -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div class="p-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div class="flex items-center gap-2.5">
          <div class="w-8 h-8 rounded-lg bg-red-100 text-red-600 flex items-center justify-center">
            <i class="fa fa-hourglass-half text-sm" />
          </div>
          <div>
            <h3 class="text-sm font-bold text-gray-900">
              ¿Cuál es el producto que más rápido se acaba?
            </h3>
            <p class="text-xs text-gray-500">
              Velocidad de venta (run-rate diario) vs stock disponible y días estimados de cobertura
            </p>
          </div>
        </div>

        <span class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-red-50 text-red-700 border border-red-200">
          Alta Rotación / Riesgo de Agotamiento
        </span>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full text-left text-xs">
          <thead class="bg-gray-50 text-gray-500 uppercase font-bold text-[10px] border-b border-gray-100">
            <tr>
              <th class="py-3 px-4 text-center" style="width: 5%;">#</th>
              <th class="py-3 px-4" style="width: 32%;">Producto</th>
              <th class="py-3 px-3">Código</th>
              <th class="py-3 px-3 text-right">Vendido en Período</th>
              <th class="py-3 px-3 text-right">Ritmo Diario</th>
              <th class="py-3 px-3 text-right">Stock Actual</th>
              <th class="py-3 px-3 text-center">Días de Cobertura</th>
              <th class="py-3 px-3 text-center">Tasa Absorción</th>
              <th class="py-3 px-4 text-center">Nivel de Urgencia</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr
              v-for="(item, idx) in inventory.fastest_depleting"
              :key="item.id"
              class="hover:bg-red-50/20 transition-colors"
            >
              <td class="py-3 px-4 text-center font-bold text-gray-400">
                <span
                  class="w-5 h-5 rounded-full inline-flex items-center justify-center text-[10px]"
                  :class="idx === 0 ? 'bg-red-600 text-white font-black shadow-sm' : idx < 3 ? 'bg-red-100 text-red-800 font-bold' : 'bg-gray-100 text-gray-600'"
                >
                  {{ idx + 1 }}
                </span>
              </td>
              <td class="py-3 px-4">
                <div class="font-bold text-gray-900">{{ item.name }}</div>
                <div class="text-[10px] text-gray-400 font-mono">{{ item.code }}</div>
              </td>
              <td class="py-3 px-3 font-mono text-gray-500">
                {{ item.code }}
              </td>
              <td class="py-3 px-3 text-right font-mono font-bold text-orange-600">
                {{ formatNumber(item.units_sold) }}
              </td>
              <td class="py-3 px-3 text-right font-mono text-gray-700">
                {{ item.daily_velocity }} / día
              </td>
              <td class="py-3 px-3 text-right font-mono font-bold" :class="item.current_stock <= 0 ? 'text-red-600' : 'text-gray-900'">
                {{ formatNumber(item.current_stock) }}
              </td>
              <td class="py-3 px-3 text-center font-mono font-bold text-sm">
                <span v-if="item.days_coverage === 0" class="text-red-600 animate-pulse">
                  0 días (Agotado)
                </span>
                <span v-else-if="item.days_coverage >= 999" class="text-gray-400 text-xs">
                  Sin rotación
                </span>
                <span v-else-if="item.days_coverage <= 7" class="text-rose-600">
                  {{ item.days_coverage }} días
                </span>
                <span v-else-if="item.days_coverage <= 15" class="text-amber-600">
                  {{ item.days_coverage }} días
                </span>
                <span v-else class="text-emerald-600">
                  {{ item.days_coverage }} días
                </span>
              </td>
              <td class="py-3 px-3 text-center font-mono text-gray-600">
                {{ item.depletion_rate }}%
              </td>
              <td class="py-3 px-4 text-center">
                <span
                  class="px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wide inline-flex items-center gap-1 shadow-xs"
                  :class="[
                    item.urgency === 'exhausted' ? 'bg-red-100 text-red-800 border border-red-200' :
                    item.urgency === 'critical' ? 'bg-orange-100 text-orange-800 border border-orange-200' :
                    item.urgency === 'warning' ? 'bg-amber-100 text-amber-800 border border-amber-200' :
                    'bg-emerald-100 text-emerald-800 border border-emerald-200'
                  ]"
                >
                  <i
                    class="fa text-[9px]"
                    :class="[
                      item.urgency === 'exhausted' ? 'fa-ban' :
                      item.urgency === 'critical' ? 'fa-exclamation-triangle' :
                      item.urgency === 'warning' ? 'fa-clock' : 'fa-check'
                    ]"
                  />
                  <span>{{ item.urgency_label }}</span>
                </span>
              </td>
            </tr>
            <tr v-if="!inventory.fastest_depleting?.length">
              <td colspan="9" class="text-center py-6 text-gray-400">
                No hay ventas suficientes para calcular la velocidad de rotación.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- SECCIÓN 3: ALERTAS DE STOCK BAJO / POR AGOTARSE -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div class="p-4 border-b border-gray-100 flex items-center justify-between">
        <div class="flex items-center gap-2">
          <i class="fa fa-bell text-rose-500" />
          <h3 class="text-sm font-bold text-gray-900">Productos con Stock Crítico (≤ 10 unidades)</h3>
        </div>
        <span class="text-xs text-rose-600 font-semibold">
          Requieren reposición inmediata
        </span>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full text-left text-xs">
          <thead class="bg-gray-50 text-gray-500 uppercase font-bold text-[10px] border-b border-gray-100">
            <tr>
              <th class="py-2.5 px-4">Código</th>
              <th class="py-2.5 px-4">Producto</th>
              <th class="py-2.5 px-3">Categoría</th>
              <th class="py-2.5 px-3 text-right">Costo Unit.</th>
              <th class="py-2.5 px-3 text-right">Precio Venta</th>
              <th class="py-2.5 px-4 text-center">Stock Actual</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr
              v-for="prod in inventory.low_stock_alerts"
              :key="prod.id"
              class="hover:bg-rose-50/20 transition-colors"
            >
              <td class="py-2.5 px-4 font-mono text-gray-500 font-bold">
                {{ prod.code }}
              </td>
              <td class="py-2.5 px-4 font-bold text-gray-900">
                {{ prod.name }}
              </td>
              <td class="py-2.5 px-3 text-gray-600">
                {{ prod.category }}
              </td>
              <td class="py-2.5 px-3 text-right font-mono text-gray-500">
                {{ formatCurrency(prod.cost) }}
              </td>
              <td class="py-2.5 px-3 text-right font-mono font-bold text-gray-800">
                {{ formatCurrency(prod.price) }}
              </td>
              <td class="py-2.5 px-4 text-center">
                <span
                  class="px-2.5 py-0.5 rounded-full text-xs font-black"
                  :class="prod.quantity <= 0 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'"
                >
                  {{ formatNumber(prod.quantity) }} un.
                </span>
              </td>
            </tr>
            <tr v-if="!inventory.low_stock_alerts?.length">
              <td colspan="6" class="text-center py-6 text-emerald-600 font-medium">
                <i class="fa fa-check-circle mr-1 text-emerald-500" />
                Excelente: Todos los productos tienen existencias por encima de 10 unidades.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InventoryRotationTab',

  props: {
    inventory: {
      type: Object,
      required: true,
    },
    filters: {
      type: Object,
      required: true,
    },
  },

  methods: {
    getPdfUrl(type) {
      const from = this.filters.date_from || ''
      const to = this.filters.date_to || ''
      return `/reports/export/pdf?type=${type}&date_from=${from}&date_to=${to}`
    },

    formatCurrency(val) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
      }).format(val || 0)
    },

    formatNumber(val) {
      return new Intl.NumberFormat('es-ES', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(val || 0)
    },
  },
}
</script>
